import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft } from "lucide-react";
import ArrowUpIcon from "../../assets/icons/ArrowUpIcon";
import ChevronDownIcon from "../../assets/icons/ChevronDownIcon";
import SotravelIcon from "../../assets/icons/SotravelIcon";
import { faqs } from "../../constants/landing-new";

import GoogleReviewIcon from "../../assets/icons/GoogleReviewIcon";
import StarIcon from "../../assets/icons/StarIcon";
import { companyReviews } from "../../constants/landing-new";

import { useTabletOrMobile, useIsDesktop } from "../../hooks/responsive";
import { AutoPlaySilentVideo } from "../../components/video";
import { communityURL, contactUsURL } from "../../utils/constants";
import { useUserLocale } from "../../customHooks/useUserLocale";
import { useShouldShowVideo } from "../../customHooks/useShouldShowVideo";
import { isMobile } from "react-device-detect";
import { useIsInApp } from "../../customHooks/useIsInApp";
import { APP_HOME, goBackSafe } from "../../utils/appShell";
import { isUserInChinaSync } from "../../utils/chinaDetection";

export const StickyHeader = ({ withoutCta }) => {
  const { t } = useTranslation();
  const { language, community, currency, loading, handleChangeLocale } = useUserLocale();
  const isInApp = useIsInApp();
  const navigate = useNavigate();

  return (
    <>
      <div className="tw-fixed tw-left-0 tw-right-0 tw-top-0 tw-z-20 tw-flex tw-items-center tw-justify-between tw-gap-4 tw-bg-black/10 tw-p-4 tw-backdrop-blur-sm md:tw-px-12 md:tw-py-4">
        <div className="tw-grow md:tw-grow-0">
          {isInApp ? (
            // In the app there's no site to go "home" to: back to where the
            // page was opened from, else the Explore feed.
            <button
              type="button"
              aria-label={t("back", { defaultValue: "Back" })}
              onClick={() => goBackSafe(navigate, APP_HOME)}
              className="tw-flex tw-size-9 tw-items-center tw-justify-center tw-rounded-full tw-bg-black/30 tw-text-white"
            >
              <ChevronLeft className="tw-size-6" />
            </button>
          ) : (
          <Link to="/all-trips">
            {community === "my" ? (
              <SotravelIcon className="tw-h-8 md:tw-h-8" />
            ) : (
              // <div className="tw-text-white tw-font-bold tw-text-lg tw-flex tw-items-center">
              //   {/* <SotravelIcon className="tw-h-8 md:tw-h-8 tw-mr-2" />
              //   <span className="tw-ml-2">AVIDER峰狂</span> */}

              //   <img
              //     src="/new_logo/avider/avider_en_logo_white.png"
              //     style={{
              //       height: "16px",
              //       margin: "6px",
              //     }}
              //     alt="Avider En Logo"
              //   />
              //   <img
              //     src="/new_logo/avider/avider_cn_logo_white.png"
              //     style={{ height: "30px", margin: "6px" }}
              //     alt="Avider Cn Logo"
              //   />
              // </div>
              <SotravelIcon className="tw-h-8 md:tw-h-8" />
            )}
          </Link>
          )}
        </div>

        {!withoutCta && (
          <Link to="/all-trips">
            <button className="hover:tw-bg-primary-dark tw-rounded-full tw-bg-primary-main tw-px-4 tw-py-2 tw-text-sm tw-font-bold tw-text-white tw-transition-colors md:tw-px-8 md:tw-py-3 md:tw-text-base">
              {t("landing.sticky_header.view_trips")}
            </button>
          </Link>
        )}
      </div>
    </>
  );
};

export const HeroFeatured = ({
  desktopVideo,
  mobileVideo,
  desktopImage,
  mobileImage,
  mainText,
  subText,
  heroLogo,
  ctaText,
  ctaLink,
  onCtaClick,
}) => {
  const { t } = useTranslation();
  const isMobile = useTabletOrMobile();
  const isDesktop = useIsDesktop();
  const shouldShowVideo = useShouldShowVideo({ requireWifi: false });
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  return (
    <div style={{ marginBottom: "3.5rem" }} className="tw-h-[calc(100vh)]">
      <div className="tw-relative">
        {/* Hero Text - Responsive styling */}
        <div
          className={`tw-absolute tw-left-0 tw-right-0 tw-z-10 tw-mx-auto tw-max-w-3xl tw-px-4 ${
            heroLogo
              ? "tw-top-1/4 -tw-translate-y-1/2 tw-transform"
              : "tw-left-4 tw-right-4 tw-top-[calc(48px+100px)] md:tw-left-[15%] md:tw-right-[15%] md:tw-top-[38vh]"
          }`}
        >
          <h1 className="tw-mb-3 tw-text-center tw-text-[22px] tw-font-extrabold tw-text-white md:tw-mt-4 md:tw-text-5xl">
            {mainText}
          </h1>
          <p className="tw-text-center tw-text-[12px] tw-font-semibold tw-text-white md:tw-text-2xl">
            {subText}
          </p>
        </div>

        {/* CTA Section - Responsive positioning and styling */}
        <div className="tw-absolute tw-bottom-[calc(24px+84px)] tw-left-4 tw-right-4 tw-z-10 md:tw-bottom-[20vh] md:tw-left-[15%] md:tw-right-[15%]">
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-text-white md:tw-flex-row md:tw-gap-8">
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
              <div className="tw-text-sm md:tw-text-base">{t("landing.hero.stars")}</div>
              <div className="tw-flex tw-items-center tw-gap-0.5">
                <StarIcon className="md:tw-h-6 md:tw-w-6" />
                <StarIcon className="md:tw-h-6 md:tw-w-6" />
                <StarIcon className="md:tw-h-6 md:tw-w-6" />
                <StarIcon className="md:tw-h-6 md:tw-w-6" />
                <StarIcon className="md:tw-h-6 md:tw-w-6" />
              </div>
              <GoogleReviewIcon className="md:tw-h-6 md:tw-w-6" />
            </div>
            {ctaLink ? (
              <Link to={ctaLink} onClick={onCtaClick}>
                <button className="hover:tw-bg-primary-dark tw-rounded-full tw-bg-primary-main tw-px-6 tw-py-3 tw-text-base tw-font-bold tw-text-white tw-transition-colors md:tw-px-10 md:tw-py-4 md:tw-text-lg">
                  {ctaText}
                </button>
              </Link>
            ) : (
              <button
                onClick={onCtaClick}
                className="hover:tw-bg-primary-dark tw-rounded-full tw-bg-primary-main tw-px-6 tw-py-3 tw-text-base tw-font-bold tw-text-white tw-transition-colors md:tw-px-10 md:tw-py-4 md:tw-text-lg"
              >
                {ctaText}
              </button>
            )}
          </div>
        </div>

        {/* Hero Video/Image - Responsive sizing and overlay */}
        <div className="tw-relative tw-h-[calc(100vh)] tw-w-full">
          <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-b tw-from-black/10 tw-to-black/10"></div>

          {isDesktop && (
            <>
              {shouldShowVideo ? (
                <AutoPlaySilentVideo
                  className="tw-h-full tw-w-full tw-object-cover tw-object-center md:tw-block"
                  video={desktopVideo}
                  poster={desktopImage}
                />
              ) : (
                <img
                  src={desktopImage}
                  className="tw-h-full tw-w-full tw-object-cover tw-object-center md:tw-block"
                />
              )}
            </>
          )}

          {isMobile && (
            <>
              {shouldShowVideo ? (
                <AutoPlaySilentVideo
                  className="tw-h-full tw-w-full tw-object-cover tw-object-center"
                  video={mobileVideo}
                  poster={mobileImage}
                />
              ) : (
                <img
                  src={mobileImage}
                  className="tw-h-full tw-w-full tw-object-cover tw-object-center"
                />
              )}
            </>
          )}

          {/* Hero Logo Overlay */}
          {heroLogo && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
              className="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center"
            >
              <motion.img
                src={heroLogo}
                alt="Hero logo"
                animate={{
                  y: [0, -8, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="tw-w-[280px] tw-drop-shadow-2xl md:tw-w-[420px]"
              />
            </motion.div>
          )}
        </div>

        {/* Company Reviews Section - Responsive layout */}
        <div className="tw-absolute tw-left-0 tw-right-0 tw-top-[calc(100vh-55.5px)] tw-z-10">
          <div className="tw-container tw-mx-auto">
            <div className="tw-flex tw-gap-4 tw-overflow-x-auto tw-px-4 tw-pb-4 [-ms-overflow-style:none] [scrollbar-width:none] md:tw-justify-center [&::-webkit-scrollbar]:tw-hidden">
              {companyReviews.map(({ review, imageSrc, key }) => (
                <div
                  className="tw-flex tw-w-[218.25px] tw-flex-none tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-2xl tw-bg-white tw-p-3 tw-shadow-md md:tw-max-w-[250px] md:tw-flex-1"
                  key={key}
                >
                  <img alt="review" src={imageSrc} className="tw-w-[103.23px] md:tw-w-[120px]" />
                  <div className="tw-text-center tw-text-xs tw-text-[#6C757D] md:tw-text-sm">
                    {t(`landing.company_reviews.${key}`)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const WhyChooseUs = ({
  className,
  mobileVariant = "vertical",
  title,
  description,
  reasons,
  removeCta = false,
  cta,
  ctaType,
  darkerPicture = false,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={`tw-bg-white ${
        mobileVariant === "horizontal"
          ? "tw-py-[30px] md:tw-px-16 md:tw-py-10 lg:tw-px-24"
          : "tw-px-4 tw-py-[30px] md:tw-px-16 md:tw-py-10 lg:tw-px-24"
      } ${className || ""}`}
    >
      <div className="tw-text-center tw-text-[26px] tw-font-extrabold md:tw-text-[32px]">
        {title}
      </div>
      <div className="tw-mb-8 tw-px-2 tw-text-center tw-text-base tw-font-semibold tw-text-primary-main md:tw-mb-12 md:tw-px-0 md:tw-text-lg">
        {description}
      </div>
      <div
        className={`${
          mobileVariant === "horizontal"
            ? "tw-flex tw-snap-x tw-snap-mandatory tw-overflow-x-auto tw-px-4 [-ms-overflow-style:none] [scrollbar-width:none] md:tw-px-0 [&::-webkit-scrollbar]:tw-hidden"
            : "tw-flex tw-flex-col"
        } tw-gap-7 md:tw-grid md:tw-grid-cols-[repeat(auto-fit,minmax(250px,1fr))] md:tw-justify-center md:tw-gap-6`}
      >
        {reasons.map((card) => (
          <div
            key={card.key || card.title}
            className={`${
              mobileVariant === "horizontal"
                ? "tw-w-[85vw] tw-flex-none tw-snap-center"
                : "tw-w-full"
            } md:tw-w-full`}
          >
            <ChooseUsCard
              {...card}
              mobileVariant={mobileVariant}
              translationKey={card.key}
              darkerPicture={darkerPicture}
            />
          </div>
        ))}
      </div>
      {!removeCta && (
        <div className="tw-flex tw-justify-center">
          {cta ? (
            ctaType === "pass_bundle" ? (
              <button
                onClick={cta}
                style={{ border: "5px solid rgba(235, 208, 53, 0.78)" }}
                className="tw-w-65 tw-mt-10 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-[#FFD700] tw-px-16 tw-py-3 tw-text-base tw-font-bold tw-text-[#6C757D] tw-transition-transform hover:tw-scale-105 md:tw-mt-12"
              >
                <LocalActivityIcon
                  style={{ fontSize: "24px", color: "white" }}
                  className="tw-mr-4"
                />{" "}
                <span>{t("landing.destination.purchase_pass")}</span>
              </button>
            ) : (
              <button
                onClick={cta}
                className="tw-w-70 hover:tw-bg-primary-dark tw-mt-10 tw-rounded-2xl tw-bg-primary-main tw-px-16 tw-py-3 tw-text-base tw-font-bold tw-text-white tw-transition-colors md:tw-mt-12"
              >
                {t("landing.why_choose_us.explore_trips")}
              </button>
            )
          ) : (
            <Link to="/all-trips">
              <button className="hover:tw-bg-primary-dark tw-mt-10 tw-w-full tw-rounded-2xl tw-bg-primary-main tw-px-16 tw-py-3 tw-text-base tw-font-bold tw-text-white tw-transition-colors md:tw-mt-12">
                {t("landing.why_choose_us.explore_trips")}
              </button>
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export const ChooseUsCard = ({
  title,
  description,
  thumbnailSrc,
  videoSrc,
  reviewer,
  gradient,
  mobileVariant,
  translationKey,
  darkerPicture,
}) => {
  const { t } = useTranslation();

  // Determine title content based on what's available
  const getTitleContent = () => {
    // If title is a getter function (from the updated constants)
    if (typeof title === "object" && title !== null && typeof title.title === "function") {
      return title.title;
    }
    // If using translationKey approach
    if (translationKey) {
      return t(`landing.why_choose_us.reasons.${translationKey}.title`);
    }
    // Fallback to direct title prop
    return title;
  };

  // Determine description content based on what's available
  const getDescriptionContent = () => {
    // If description is a getter function (from the updated constants)
    if (
      typeof description === "object" &&
      description !== null &&
      typeof description.description === "function"
    ) {
      return description.description;
    }
    // If using translationKey approach
    if (translationKey) {
      return t(`landing.why_choose_us.reasons.${translationKey}.description`);
    }
    // Fallback to direct description prop
    return description;
  };

  // Parse HTML content from translation if it's a string
  const renderHTML = (content) => {
    if (typeof content === "string" && content.includes("<span")) {
      return <div dangerouslySetInnerHTML={{ __html: content }} />;
    }
    return content;
  };

  return (
    <div
      className={`tw-relative tw-h-[400px] tw-rounded-3xl tw-drop-shadow-[0px_4px_4px_0px_#00000040]`}
    >
      {videoSrc ? (
        <video
          autoPlay
          loop
          muted
          playsInline
          className="tw-h-[400px] tw-w-full tw-rounded-3xl tw-object-cover"
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
      ) : (
        <img
          alt="review"
          className="tw-h-[400px] tw-w-full tw-rounded-3xl tw-object-cover"
          style={darkerPicture ? { filter: "brightness(0.7)" } : {}}
          src={thumbnailSrc}
        />
      )}
      <div className="tw-absolute tw-inset-0 tw-rounded-3xl" style={{ background: gradient }} />
      {/* Legibility scrims behind the title (top) and description (bottom),
          only on video cards — bright video frames (e.g. snow) wash out the
          white text. Image cards read fine as-is, so they're left untouched. */}
      {videoSrc && (
        <>
          <div
            className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-0 tw-h-2/5 tw-rounded-t-3xl"
            style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.6), rgba(0,0,0,0))" }}
          />
          <div
            className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-2/5 tw-rounded-b-3xl"
            style={{
              background:
                "linear-gradient(to top, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0) 75%)",
            }}
          />
        </>
      )}
      <div className="tw-absolute tw-left-4 tw-right-4 tw-top-6 md:tw-top-8">
        <div
          className="tw-text-center tw-text-2xl tw-font-extrabold tw-text-white md:tw-text-3xl"
          style={{ textShadow: "1px 1px 3px rgba(0,0,0,0.7)" }}
        >
          {getTitleContent()}
        </div>
      </div>
      <div className="tw-absolute tw-bottom-4 tw-left-4 tw-right-4 tw-flex tw-flex-col tw-gap-2 md:tw-bottom-6">
        <div
          className="tw-text-center tw-text-sm tw-font-medium tw-text-white md:tw-text-base"
          style={{ textShadow: "1px 1px 3px rgba(0,0,0,0.7)" }}
        >
          {renderHTML(getDescriptionContent())}
        </div>
        {reviewer && (
          <div>
            <div className="tw-mb-0.5 tw-text-center tw-text-[10px] tw-text-white md:tw-text-xs">
              {reviewer.name}
            </div>
            <div className="tw-flex tw-justify-center">
              <img
                className="tw-h-5 tw-w-5 tw-rounded-full tw-border-2 tw-border-white tw-object-cover md:tw-h-6 md:tw-w-6"
                alt="person"
                src={reviewer.profilePictureSrc}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const FAQs = ({ customFaqs = null }) => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="tw-px-4 tw-py-10 md:tw-px-16 md:tw-py-10 lg:tw-px-24">
      <div className="tw-text-center tw-text-[26px] tw-font-extrabold md:tw-text-[32px]">
        {t("landing.faqs.title")}
      </div>
      <div className="tw-mb-6 tw-text-center tw-text-base tw-font-semibold tw-text-primary-main md:tw-mb-10 md:tw-text-lg">
        {t("landing.faqs.subtitle")}
      </div>
      <div className="md:tw-mx-auto md:tw-max-w-3xl">
        {(customFaqs || faqs).map((faq, index) => (
          <div
            key={index}
            className={`tw-border-x tw-border-b tw-border-[#E9ECEF] ${
              index === faqs.length - 1 ? "tw-rounded-bl-lg tw-rounded-br-lg" : ""
            } ${index === 0 && "tw-rounded-tl-lg tw-rounded-tr-lg tw-border-t"}`}
          >
            <button
              onClick={() => toggleFAQ(index)}
              className={`tw-flex tw-w-full tw-items-center tw-gap-2 tw-px-4 tw-py-3 tw-transition-colors hover:tw-bg-[#F8F9FA] md:tw-px-6 md:tw-py-4 ${
                openIndex === index ? "tw-bg-[#F8F9FA]" : ""
              }`}
            >
              <div className="tw-grow tw-text-left tw-text-sm md:tw-text-base">{faq.question}</div>
              <motion.div
                animate={{ rotate: openIndex === index ? 180 : 0 }}
                transition={{ duration: 0.3 }}
                className="tw-flex-none tw-text-[#CED4DA]"
              >
                <ChevronDownIcon className="tw-h-5 tw-w-5 md:tw-h-6 md:tw-w-6" />
              </motion.div>
            </button>
            <AnimatePresence>
              {openIndex === index && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="tw-overflow-hidden"
                >
                  <div className="tw-border-t tw-border-[#E9ECEF] tw-bg-[#F8F9FA] tw-px-4 tw-py-3 tw-text-sm md:tw-px-6 md:tw-py-4 md:tw-text-base">
                    {faq.answer}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const { language, community, currency, loading, handleChangeLocale } = useUserLocale();
  const isInApp = useIsInApp();

  if (isInApp) return null;

  return (
    <div className="tw-mt-auto tw-w-full tw-px-4 tw-py-10 md:tw-px-16 md:tw-py-16">
      <div
        className="tw-mb-6 tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-transition-opacity hover:tw-opacity-80"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      >
        <div className="tw-border[#E9ECEF] tw-grow tw-border-t"></div>
      </div>

      <div className="md:tw-grid md:tw-grid-cols-12 md:tw-gap-12">
        {/* Logo and Contact Section */}
        <div className="md:tw-col-span-3">
          <div className="tw-mb-8 tw-flex tw-items-center tw-gap-2">
            <SotravelIcon size={34} color="#00C1FD" />
            <div className="tw-text-[25px] tw-font-bold tw-text-primary-main">
              {t("landing.footer.company_name")}
            </div>
          </div>

          <div className="tw-mb-4">
            <div className="tw-text[#212529] tw-mb-2 tw-text-sm tw-font-bold md:tw-text-base">
              {t("landing.footer.inquiry_title")}
            </div>
            <a
              href={contactUsURL}
              target="_blank"
              className="tw-block tw-text-xs tw-text-primary-7 hover:tw-underline md:tw-text-sm"
              rel="noreferrer"
            >
              {t("landing.footer.contact_us")}
            </a>
          </div>

          <div className="tw-mb-6">
            <div className="tw-text[#212529] tw-mb-2 tw-text-sm tw-font-bold md:tw-text-base">
              {t("landing.footer.collab_title")}
            </div>
            <a
              href="mailto:community@sotravel.com"
              className="tw-block tw-text-xs tw-text-primary-7 hover:tw-underline md:tw-text-sm"
            >
              community@sotravel.com
            </a>
          </div>
        </div>

        {/* Location and Quick Links Section */}
        <div className="md:tw-col-span-3">
          <div className="tw-mb-6">
            <div className="tw-text[#212529] tw-mb-4 tw-text-sm tw-font-bold md:tw-text-base">
              {t("landing.footer.quick_links")}
            </div>
            <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-1 md:tw-gap-y-3">
              <div>
                <Link
                  to="/our-leader-programme"
                  className="tw-mb-2 tw-text-sm tw-text-[#212529] tw-transition-colors hover:tw-text-primary-main md:tw-mb-0 md:tw-text-base"
                >
                  {t("landing.footer.become_leader")}
                </Link>
              </div>
              <div>
                <Link
                  to="/all-trips"
                  className="tw-mb-2 tw-text-sm tw-text-[#212529] tw-transition-colors hover:tw-text-primary-main md:tw-mb-0 md:tw-text-base"
                >
                  {t("landing.footer.view_trips")}
                </Link>
              </div>
              <div>
                {/* /guides lives outside this SPA — a plain link (full page load) that crawlers can follow */}
                <a
                  href="/guides"
                  className="tw-mb-2 tw-text-sm tw-text-[#212529] tw-transition-colors hover:tw-text-primary-main md:tw-mb-0 md:tw-text-base"
                >
                  {t("landing.footer.travel_guides")}
                </a>
              </div>
              {/* The AI coach tool page is part of the /guides app too — plain link, same as above.
                  zh-Hant has its own page (zh-Hans has none, so it gets the English one). Hidden on the
                  China build: www.sotravel.me does not proxy /guides. */}
              {!isUserInChinaSync() && (
                <div>
                  <a
                    href={
                      i18n.language === "zh-Hant-TW"
                        ? "/guides/zh-hant/ai-ski-snowboard-coach"
                        : "/guides/ai-ski-snowboard-coach"
                    }
                    className="tw-mb-2 tw-text-sm tw-text-[#212529] tw-transition-colors hover:tw-text-primary-main md:tw-mb-0 md:tw-text-base"
                  >
                    {t("landing.footer.ai_ski_snowboard_coach", {
                      defaultValue: "AI Ski & Snowboard Coach",
                    })}
                  </a>
                </div>
              )}
              <div>
                <a
                  href="https://blog.sotravel.com"
                  target="_blank"
                  className="tw-mb-2 tw-text-sm tw-text-[#212529] tw-transition-colors hover:tw-text-primary-main md:tw-mb-0 md:tw-text-base"
                  rel="noreferrer"
                >
                  {t("landing.footer.read_blog")}
                </a>
              </div>
              <div>
                <Link to="/socials">
                  <span className="tw-mb-2 tw-text-sm tw-text-[#212529] tw-transition-colors hover:tw-text-primary-main md:tw-mb-0 md:tw-text-base">
                    {t("landing.footer.follow_socials")}
                  </span>
                </Link>
              </div>
              <div>
                <Link to="/company-information">
                  <span className="tw-mb-2 tw-text-sm tw-text-[#212529] tw-transition-colors hover:tw-text-primary-main md:tw-mb-0 md:tw-text-base">
                    {t("landing.footer.corporate_information")}
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Newsletter Section */}
        {community == "tw" ? (
          <div className="md:tw-col-span-6">
            <div className="tw-relative">
              <div
                className={`tw-h-[231px] tw-w-full tw-rounded-lg md:tw-h-[270px] ${
                  isMobile ? "tw-mb-20" : ""
                }`}
                style={{
                  backgroundColor: isMobile ? "#f5f5f5" : "transparent",
                }}
              >
                <div className="tw-flex tw-h-full tw-flex-col tw-p-6">
                  <div className="tw-mb-1 tw-text-left tw-text-xl tw-font-extrabold tw-text-gray-800 md:tw-text-right md:tw-text-2xl">
                    Singapore (Global HQ)
                  </div>
                  <div className="tw-mb-3 tw-text-left tw-font-bold tw-text-gray-700 md:tw-text-right">
                    Social Travel Pte. Ltd.
                  </div>

                  <div className="tw-text-left tw-text-sm tw-text-gray-700 md:tw-text-right md:tw-text-base">
                    統一編號: 202346815N
                  </div>
                  <div className="tw-text-left tw-text-sm tw-text-gray-700 md:tw-text-right md:tw-text-base">
                    旅行社執照: TA03798
                  </div>
                  <div className="tw-text-left tw-text-sm tw-text-gray-700 md:tw-text-right md:tw-text-base">
                    辦公室地址: 150D East Coast Rd
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="md:tw-col-span-6">
            <div className="tw-relative">
              <img
                alt="newsletter"
                className="tw-h-[201px] tw-w-full tw-rounded-lg tw-object-cover md:tw-h-[260px]"
                src="https://d1puf2nnbcyl9q.cloudfront.net/1724430195075_blob.jpeg"
              />
              <div className="tw-absolute tw-inset-0 tw-rounded-lg tw-bg-[#0000004D]/30"></div>
              <div className="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-6">
                <div className="tw-mb-6 tw-text-center tw-text-xl tw-font-extrabold tw-text-white md:tw-text-2xl">
                  {t("landing.footer.join_community_title")}
                </div>

                <Link to={communityURL} target="_blank" className="tw-block tw-w-full md:tw-w-2/3">
                  <button className="hover:tw-bg-primary-dark tw-mx-auto tw-block tw-w-3/4 tw-rounded-lg tw-bg-primary-main tw-px-6 tw-py-3 tw-text-base tw-font-bold tw-text-white tw-transition-colors md:tw-w-full md:tw-text-lg">
                    {t("landing.footer.join_community")}
                  </button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
